import {ChangeDetectionStrategy, Component, DestroyRef, Inject, InjectionToken, input, Input} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {GruppeId, MedlemsRolleModell, OpprettGruppeInvitasjonSpesifikasjon, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatRadioModule} from "@angular/material/radio";
import {MatIconModule} from "@angular/material/icon";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {ErrorStateMatcher, MatOptionModule} from "@angular/material/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatSelectModule} from "@angular/material/select";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {TranslateModule} from "@ngx-translate/core";
import {epost, litenStreng, mobilnummer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {MobilnummerFeltKomponent} from "web-ng2/delt/komponenter/mobilnummer-felt/mobilnummer-felt";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {MatTooltip} from "@angular/material/tooltip";
import {MatCheckbox} from "@angular/material/checkbox";
import {muligeTilgangsrollerForMedlem, TilgangsrollerMedlemInfo, ValiderEpostRespons} from "felles-ng2/modell/medlemModellHjelpere";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {Temporal} from "temporal-polyfill";
import {erIkkeTomListe} from "felles-ng2/støttefunksjoner/lister";
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {VisAlltidErrorStateMatcher} from "felles-ng2/infrastruktur/material/VisAlltidErrorStateMatcher";
import {skrivPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('MedlemImporterSkjemalinjeKomponent');

/**  InjectionToken for injisering av {@link MedlemImporterSkjemalinjeKomponentAvhengigheter} implementasjon. */
export const MedlemImporterSkjemalinjeKomponentAvhengigheter_DI_TOKEN = new InjectionToken<MedlemImporterSkjemalinjeKomponentAvhengigheter>('MedlemImporterSkjemalinjeKomponentAvhengigheter implementasjon');

type SkjemaType = ReturnType<typeof MedlemImporterSkjemalinjeKomponent.opprettSkjema>;
type SkjemaVerdier = SkjemaType['value'];

export type MedlemImporterSkjemalinjeType = SkjemaType
export type MedlemImporterSkjemalinjeVerdier = SkjemaVerdier

@Component({
    selector: 'medlem-importer-skjemalinje',
    templateUrl: './medlem-importer-skjemalinje.html',
    styleUrl: './medlem-importer-skjemalinje.scss',
    providers: [
        //sørger for at valideringsfeil vises selvom bruker ikke har rørt feltet
        { provide: ErrorStateMatcher, useClass: VisAlltidErrorStateMatcher }
    ],
    imports: [
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatRadioModule,
        MatIconModule,
        MatDatepickerModule,
        ReactiveFormsModule,
        MatOptionModule,
        MatSelectModule,
        TranslateModule,
        MobilnummerFeltKomponent,
        StandardValideringsfeilPipe,
        MatTooltip,
        MatCheckbox
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemImporterSkjemalinjeKomponent {

    static opprettSkjema(destroyRef: DestroyRef) {
        const skjema = new FormGroup({
            valgt: new FormControl(true, {nonNullable: true}),
            navn: new FormControl<string | null>(null, {validators: litenStreng}),
            epost: new FormControl('', {nonNullable: true, validators: [Validators.required, ...epost]}),
            mobilnummer: new FormControl<string | null>(null, {validators: mobilnummer}),
            rolle: new FormControl<MedlemsRolleModell | null>(null, {validators: Validators.required}),
            forespurtRolle: new FormControl<string|null>(null), //denne er kun med for å kunne vise info om opprinnelig forespurt rolle
            rolleStartdato: new FormControl<PlainDate | null>(null, {validators: Validators.required}),
            tilgangsroller: new FormControl<TilgangsrolleMedlem[]>(muligeTilgangsrollerForMedlem().filter(innslag => innslag.obligatorisk).map(innslag => innslag.rolle)),
            oppdaterMoetemal: new FormControl(true),
            skalSignereSomStandard: new FormControl(true)
        });
        //synkroniser skalSignereSomStandard med endringer i oppdaterMoetemal
        skjema.controls.oppdaterMoetemal.valueChanges
          .pipe(takeUntilDestroyed(destroyRef))
          .subscribe(valgt=> skjema.controls.skalSignereSomStandard.setValue(valgt));
        return skjema;
    }

    /** aktiverer/de-aktiverer angitt linje.
     * Deaktivert linje vil ikke bli inkludert i omsluttende skjema.value */
    static setSkjemalinjeValgt(linje: SkjemaType, valgt: boolean, informerOmEndring = false): SkjemaType {
        linje.controls.valgt.setValue(valgt, {emitEvent: informerOmEndring});
        valgt ? linje.enable({emitEvent: false}) : linje.disable({emitEvent: false})
        //må sørge for at valgt-elementet fortsatt er mulig å klikke på..
        linje.controls.valgt.enable({emitEvent: false});

        return linje;
    }

    static somSpesifikasjon(linje: SkjemaVerdier, gruppeId: GruppeId, gruppensTidssone: Tidssoneinformasjon): OpprettGruppeInvitasjonSpesifikasjon {
        return {
            gruppeId: gruppeId,
            navn: linje.navn || null,
            epost: linje.epost!,
            mobilnummer: linje.mobilnummer || null,
            rolleId: linje.rolle!.id,
            rolleStartdato: skrivPlainDateSerialisert(linje.rolleStartdato!),
            tilgangsroller: linje.tilgangsroller!,
            oppdaterMoetemal: linje.oppdaterMoetemal!,
            skalSignereSomStandard: linje.skalSignereSomStandard!,
            //vi eksponerer ikke oppdaterFremtidigeMoeter-valget men lar det styres av oppdaterMoetemal
            oppdaterFremtidigeMoeter: linje.oppdaterMoetemal!,
            //vi eksponerer ikke sendInvitasjon - overlater dette valget til MedlemImporterDialogKomponent
            sendInvitasjon: true,
            //vi eksponerer ikke sendInnkallingTilMoeter - overlater dette valget til MedlemImporterDialogKomponent
            sendInnkallingTilMoeter: false
        }
    }

    medlemsroller = input.required<MedlemsRolleModell[]>();
    duplikatMedlemFinnes = false;
    readonly tilgjengeligeTilgangsroller = muligeTilgangsrollerForMedlem();

    private _skjema!: SkjemaType;

    constructor(@Inject(MedlemImporterSkjemalinjeKomponentAvhengigheter_DI_TOKEN) private avhengigheter: MedlemImporterSkjemalinjeKomponentAvhengigheter,
                public enumOversettelser: EnumOversettelser,
                private destroyRef: DestroyRef) {
    }

    @Input({required: true}) set skjema(skjema: SkjemaType) {
        this._skjema = skjema;
        debug('skjema settes')

        //disable rad dersom ikke valgt
        this.skjema.controls.valgt.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(valgt =>
            MedlemImporterSkjemalinjeKomponent.setSkjemalinjeValgt(this.skjema, valgt));

        //sjekk for duplikate navn, merk at vi ikke gjør dette via validators på skjemakontroll da vi tillater duplikate,
        //men ønsker å advare om slike
        this.validerNavn(this.skjema.value.navn!);
        this.skjema.controls.navn.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(navn => this.validerNavn(navn));

        //valider epost - vi tillater ikke duplikate
        this.skjema.controls.epost.addValidators(this.epostValidator());

        //sørg for å legg til impliserte roller når tilgangsroller endres
        this.skjema.controls.tilgangsroller.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(valgte => {
              if (erNil(valgte)) return;
              //finn aktiverte roller som impliserer andre roller og aktiver disse
              const impliserteRoller = valgte
                //finn rolleinfomodellrepr.
                .map(valgt => this.finnTilgangsrollerinfo(valgt))
                //finn impliserte roller (kan returnerer null)
                .map(valgt => valgt.impliserer)
                .filter(implisert => erIkkeNil(implisert))
                //kun legg til dersom ikke allerede valgt
                .filter(implisert => !valgte.some(kandidat => kandidat == implisert));
              if (erIkkeTomListe(impliserteRoller))
                  this.skjema.controls.tilgangsroller.setValue([...valgte, ...impliserteRoller as TilgangsrolleMedlem[]]);
          });
    }

    get skjema() {
        return this._skjema;
    }

    erValgtRolle(a: MedlemsRolleModell | null, b: MedlemsRolleModell | null) {
        return a?.id === b?.id;
    }

    erValgtTilgangsrolle(a: TilgangsrollerMedlemInfo | null, b: TilgangsrollerMedlemInfo | null) {
        return a?.rolle === b?.rolle;
    }

    tilgangsrollerSomStreng() {
        if (erNil(this.skjema.value.tilgangsroller)) return '';
        return this.skjema.value.tilgangsroller
          .map(rolle => this.finnTilgangsrollerinfo(rolle))
          .map(rolleInfo => rolleInfo.tittel(this.enumOversettelser))
          .join(', ');
    }


    //hjelpmetoder

    private finnTilgangsrollerinfo(rolle: TilgangsrolleMedlem): TilgangsrollerMedlemInfo {
        return this.tilgjengeligeTilgangsroller.find(kandidat => kandidat.rolle === rolle)!;
    }

    private validerNavn(navn: string | null) {
        this.duplikatMedlemFinnes = erIkkeTomStreng(navn) && this.avhengigheter.verifiserAtNavnIkkeAlleredeErRegistrert(navn);
    }

    private epostValidator(): ValidatorFn {
        return (epostKontroller: AbstractControl<string>): ValidationErrors | null => {
            //ikke kall ekstern validering før vi har en gyldig epost-adresse
            if (epostKontroller.hasError('email') || epostKontroller.hasError('required')) return null;
            const resultat = this.avhengigheter.verifiserAtEpostIkkeAlleredeErRegistrert(epostKontroller.value);
            debug('epostValidator', epostKontroller.value, resultat)
            if (resultat.eksistererInaktivt || resultat.eksistererMedlem || resultat.eksistererInvitasjon)
                return resultat;
            else
                return null;
        }
    }
}


export interface MedlemImporterSkjemalinjeKomponentAvhengigheter {

    /** False dersom navn allerede er registrert i den aktuelle gruppen */
    verifiserAtNavnIkkeAlleredeErRegistrert(navn: string): boolean;

    /** Kontrollerer hvorvidt angitt epost allerede er registrert i den aktuelle gruppen */
    verifiserAtEpostIkkeAlleredeErRegistrert(epost: string): ValiderEpostRespons;
}
